import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { RIDE_MS, SPAN } from './world';

/**
 * Drives the camera along the line.
 *
 * Position and speed are written straight to CSS variables (`--cx`, `--v`) on
 * the root element every frame instead of going through React state: sixty
 * renders a second of the whole intro would be the slow way to move a few
 * background offsets. Everything on screen is positioned from those two
 * numbers, so the scenery, the platforms and the speed lines can never drift
 * out of step with each other.
 */
const easeInOutCubic = (u: number) => (u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2);
/** Normalised slope of the curve above: 0 at rest, 1 at its fastest. */
const speedAt = (u: number) => (u < 0.5 ? 12 * u * u : 12 * Math.pow(1 - u, 2)) / 3;

/**
 * The longer rides (a tunnel, a passing train) pull away, hold their speed
 * through the middle and then brake, so what happens out there happens at
 * speed: a quarter of the time speeding up, half cruising, a quarter braking.
 */
const ACCEL = 0.25;
const TOP = 1 / (1 - ACCEL);
const cruiseAt = (u: number) =>
  u < ACCEL
    ? (0.5 * TOP * u * u) / ACCEL
    : u > 1 - ACCEL
      ? 1 - (0.5 * TOP * (1 - u) ** 2) / ACCEL
      : 0.5 * TOP * ACCEL + TOP * (u - ACCEL);
const cruiseSpeedAt = (u: number) => Math.min(1, u / ACCEL, (1 - u) / ACCEL);

export type Pace = 'smooth' | 'cruise';

export function useTrain(rootRef: RefObject<HTMLElement | null>) {
  const raf = useRef(0);
  const at = useRef(0);

  const write = useCallback(
    (x: number, v: number) => {
      at.current = x;
      const el = rootRef.current;
      if (!el) return;
      el.style.setProperty('--cx', x.toFixed(1));
      el.style.setProperty('--v', v.toFixed(3));
    },
    [rootRef],
  );

  const place = useCallback((x: number) => write(x, 0), [write]);

  /** Roll from one position to another, speeding up and braking to a stop. Resolves on arrival. */
  const travel = useCallback(
    (from: number, to: number, ms: number, pace: Pace = 'smooth') =>
      new Promise<void>((resolve) => {
        cancelAnimationFrame(raf.current);
        const t0 = performance.now();
        const step = (now: number) => {
          const u = Math.min(1, (now - t0) / ms);
          const [x, v] = pace === 'cruise' ? [cruiseAt(u), cruiseSpeedAt(u)] : [easeInOutCubic(u), speedAt(u)];
          write(from + (to - from) * x, u >= 1 ? 0 : v);
          if (u < 1) raf.current = requestAnimationFrame(step);
          else resolve();
        };
        raf.current = requestAnimationFrame(step);
      }),
    [write],
  );

  /**
   * Pull away from wherever the camera is and keep going: speed up over
   * `accelMs` to the same top speed a stop-to-stop ride reaches, then hold it
   * until the component unmounts. This
   * is the train leaving the intro for the rider's first line, so it never
   * brakes; the host's tunnel wash takes over while it is still at speed.
   */
  const cruise = useCallback(
    (accelMs: number) => {
      cancelAnimationFrame(raf.current);
      const from = at.current;
      // easeInOutCubic peaks at 1.5x the average speed of a ride.
      const top = (1.5 * SPAN) / RIDE_MS;
      const t0 = performance.now();
      const step = (now: number) => {
        const t = now - t0;
        const x =
          t < accelMs ? (top * t ** 3) / (3 * accelMs ** 2) : (top * accelMs) / 3 + top * (t - accelMs);
        write(from + x, Math.min(1, (t / accelMs) ** 2));
        raf.current = requestAnimationFrame(step);
      };
      raf.current = requestAnimationFrame(step);
    },
    [write],
  );

  useEffect(() => () => cancelAnimationFrame(raf.current), []);
  return { place, travel, cruise };
}
